import { minorUnitExponent } from '@/kiosk-core/currency';
import type {
  EntrPhoto,
  PaymentQuote,
  PhotoSelection,
  PhotoType,
  PrepaidPool,
  PriceConfig,
  SelectionItem,
  VoucherState,
} from './types';

export const MAX_COPIES_PER_PHOTO = 20;

export function changePhotoQuantity(
  selection: PhotoSelection,
  photoId: string,
  delta: number
): PhotoSelection {
  const current = selection[photoId] ?? 0;
  const next = Math.max(0, Math.min(MAX_COPIES_PER_PHOTO, current + delta));

  if (next === current) return selection;

  const updated = { ...selection };
  if (next === 0) {
    delete updated[photoId];
  } else {
    updated[photoId] = next;
  }
  return updated;
}

export function getPhotoPriceCents(
  photo: EntrPhoto,
  prices: PriceConfig
): number {
  return photo.type === 'group' ? prices.groupPhotoCents : prices.portraitCents;
}

export function getSelectionItems(
  photos: EntrPhoto[],
  selection: PhotoSelection,
  prices: PriceConfig
): SelectionItem[] {
  return photos
    .map((photo) => {
      const quantity = selection[photo.id] ?? 0;
      if (quantity <= 0) return null;
      return {
        photo,
        quantity,
        unitPriceCents: getPhotoPriceCents(photo, prices),
      };
    })
    .filter((item): item is SelectionItem => item !== null);
}

interface QuotedCopy {
  // The position of the copy in print order: each item, then its quantity.
  index: number;
  type: PhotoType;
  priceCents: number;
}

function expandCopies(items: SelectionItem[]): QuotedCopy[] {
  return items
    .flatMap((item) =>
      Array.from({ length: item.quantity }, () => ({
        type: item.photo.type,
        priceCents: item.unitPriceCents,
      }))
    )
    .map((copy, index) => ({ ...copy, index }));
}

// Apply a prepaid pool to a set of copies: the most expensive `remaining`
// copies are covered. The sort is stable, thus of the copies with the same
// price the first in print order are covered.
function coverWithPool(copies: QuotedCopy[], pool: PrepaidPool): QuotedCopy[] {
  const remaining = Math.max(0, pool.prepaid - pool.used);
  return [...copies]
    .sort((a, b) => b.priceCents - a.priceCents)
    .slice(0, remaining);
}

function prepaidCoveredCopies(
  copies: QuotedCopy[],
  voucher: VoucherState
): QuotedCopy[] {
  if (voucher.kind === 'prepaid') {
    return coverWithPool(copies, {
      prepaid: voucher.prepaidPhotoCount,
      used: voucher.usedPhotoCount,
    });
  }
  if (voucher.kind === 'prepaid-split') {
    const ofType = (type: PhotoType) =>
      copies.filter((copy) => copy.type === type);
    return [
      ...coverWithPool(ofType('group'), voucher.group),
      ...coverWithPool(ofType('portrait'), voucher.portrait),
    ];
  }
  return [];
}

/**
 * The quote of a selection. The prepaid coverage decides each copy: the
 * declaration of the card payment and the payment of each printed copy use
 * copyAmountsCents, thus a copy that did not print gets back what the quote
 * charged for it. O(n log n) in the copies.
 */
export function calculatePaymentQuote(
  items: SelectionItem[],
  voucher: VoucherState,
  prices: PriceConfig
): PaymentQuote {
  const copies = expandCopies(items);
  const covered = prepaidCoveredCopies(copies, voucher);
  const coveredIndices = new Set(covered.map((copy) => copy.index));
  const subtotalCents = copies.reduce((sum, copy) => sum + copy.priceCents, 0);
  const prepaidDiscountCents = covered.reduce(
    (sum, copy) => sum + copy.priceCents,
    0
  );

  return {
    currency: prices.currency,
    totalSelected: copies.length,
    subtotalCents,
    prepaidAppliedCount: covered.length,
    prepaidDiscountCents,
    amountDueCents: Math.max(0, subtotalCents - prepaidDiscountCents),
    hasVoucherNote: covered.length > 0,
    copyAmountsCents: copies.map((copy) =>
      coveredIndices.has(copy.index) ? 0 : copy.priceCents
    ),
  };
}

export function formatMoney(minorUnits: number, currency: string): string {
  const exponent = minorUnitExponent(currency);
  const amount = (minorUnits / 10 ** exponent)
    .toFixed(exponent)
    .replace('.', ',');
  return `${currency.toUpperCase()} ${amount}`;
}
